export interface BusinessType {
  id: string;
  code: string;
  name: string;
  icon: string;
  description: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Plan {
  id: string;
  name: string;
  displayName: string;
  monthlyPrice: number;
  currency?: string; // JPY, THB など
  country?: string; // JP, TH など
  planType: 'reservation' | 'mobile_order' | 'combo';
  features: {
    // 予約システム機能
    reservationSystem: boolean;
    maxReservationsPerMonth: number | null; // null = 無制限
    maxStaff: number | null; // null = 無制限
    analytics: boolean;
    // モバイルオーダー機能
    mobileOrderSystem: boolean;
    maxMenuItems: number | null;
    tableManagement: boolean;
    // 共通機能
    apiAccess: boolean;
    customBranding: boolean;
    multiLanguage: boolean;
  };
  stripeProductId: string | null;
  stripePriceId: string | null;
  sortOrder: number;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface Shop {
  id: string;
  shopCode: string;
  shopName: string;
  shopNameEn?: string; // 英語店舗名
  shopNameTh?: string; // タイ語店舗名
  shopNameZh?: string; // 繁体字中国語店舗名
  shopNameKo?: string; // 韓国語店舗名
  businessTypeCode: string;
  ownerId: string;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  postalCode: string;
  prefecture: string;
  prefectureEn?: string; // 英語都道府県名
  prefectureTh?: string; // タイ語都道府県名
  city: string;
  cityEn?: string; // 英語市区町村名
  cityTh?: string; // タイ語市区町村名
  address: string;
  addressEn?: string; // 英語住所
  addressTh?: string; // タイ語住所
  building?: string;
  buildingEn?: string; // 英語建物名
  buildingTh?: string; // タイ語建物名
  phoneNumber: string;
  email: string;
  websiteUrl?: string;
  facebookUrl?: string;
  instagramUrl?: string;
  xTwitterUrl?: string;
  description?: string;
  descriptionEn?: string; // 英語説明文
  descriptionTh?: string; // タイ語説明文
  descriptionZh?: string; // 繁体字中国語説明文
  descriptionKo?: string; // 韓国語説明文
  qrCodeUrl?: string;
  fullAddress?: string;
  fullAddressEn?: string;
  fullAddressTh?: string;
  fullAddressZh?: string;
  fullAddressKo?: string;
  language: 'ja' | 'th' | 'en';
  timezone: string;
  businessDayBoundaryTime?: number; // 営業日区切り時刻（0-23, デフォルト0=深夜0時）
  currency: 'JPY' | 'THB' | 'USD';

  // Master Data Location
  location?: {
    countryCode: string;      // 'JP', 'TH'
    regionId: string;         // 'JP_KANTO', 'TH_CENTRAL'
    areaId: string;           // 'JP_KANTO_TOKYO', 'TH_CENTRAL_BANGKOK'
  };

  // Master Data Business Types (max 3 each)
  businessTypes?: {
    categories: string[];      // Category IDs
    subcategories: string[];   // Subcategory IDs
    detailedTypes: string[];   // Detailed type IDs
  };

  // モバイルオーダー用店舗設定
  defaultLanguage?: 'ja' | 'th' | 'en' | 'zh-TW' | 'ko'; // デフォルト表示言語
  priceDisplayType?: 'tax_included' | 'tax_excluded'; // 価格表示タイプ
  taxRate?: number; // 消費税率（例: 0.1 = 10%）
  reducedTaxRate?: number; // 軽減税率（例: 0.08 = 8%）
  serviceCharge?: number; // サービス料（例: 0.1 = 10%）
  creditCardFee?: number; // クレジット手数料（例: 0.03 = 3%）
  lineMessaging?: {
    channelAccessToken: string;
    channelSecret: string;
    enabled: boolean;
    features: {
      reservationConfirmation: boolean;
      reminderBeforeDay: boolean;
      cancelNotification: boolean;
    };
    adminLineUserIds?: string[]; // 店舗管理者のLINE User ID配列
  };
  // 旧営業時間（後方互換性のため残す）
  businessHours?: {
    monday: WorkingHours;
    tuesday: WorkingHours;
    wednesday: WorkingHours;
    thursday: WorkingHours;
    friday: WorkingHours;
    saturday: WorkingHours;
    sunday: WorkingHours;
  };
  // 新営業時間（複数時間帯対応）
  businessSchedule?: {
    monday: DaySchedule;
    tuesday: DaySchedule;
    wednesday: DaySchedule;
    thursday: DaySchedule;
    friday: DaySchedule;
    saturday: DaySchedule;
    sunday: DaySchedule;
  };
  // 営業カレンダー（臨時休業・特別営業時間）
  businessCalendar?: {
    specialDates?: {
      date: string;  // YYYY-MM-DD形式
      type: 'closed' | 'special_hours';  // 休業 or 特別営業時間
      label?: string;  // ラベル（例: "年末年始休業", "臨時休業"）
      timeSlots?: TimeSlot[]; // 特別営業時間の場合の時間帯
    }[];
  };
  planId: string;
  subscriptionStatus: 'active' | 'inactive' | 'trial' | 'suspended';
  monthlyReservationCount: number;
  mobileOrderEnabled?: boolean; // モバイルオーダー機能の有効化フラグ
  menuTitleCategories?: {
    id: string;
    name: string;
    nameEn?: string;
    nameTh?: string;
    nameZh?: string;
    nameKo?: string;
    imageUrl?: string;
    sortOrder: number;
  }[];
  reservationEnabled?: boolean; // 予約機能の有効化フラグ
  liffId?: string; // LINE LIFF アプリID (モバイルオーダー用)

  // Stripe Connect関連
  stripeConnectAccountId?: string; // Stripe ConnectアカウントID
  stripeConnectStatus?: 'pending' | 'active' | 'restricted' | 'disabled';
  stripeOnboardingCompleted?: boolean;
  stripePayoutsEnabled?: boolean; // 振込が有効か
  stripeChargesEnabled?: boolean; // 決済が有効か
  displaySettings?: {
    showCategoryImages?: boolean; // カテゴリ画像の表示
    showProductImages?: boolean;  // 商品画像の表示
    showOptionImages?: boolean;   // オプション画像の表示
  };

  // テーマカラー設定
  themeSettings?: {
    backgroundColor?: string;           // 背景色
    textColor?: string;                 // テキスト色
    primaryColor?: string;              // プライマリカラー
    secondaryColor?: string;            // セカンダリカラー
    headerBackgroundColor?: string;     // ヘッダー背景色
    headerTextColor?: string;           // ヘッダー文字色
    buttonColor?: string;               // ボタン背景色
    buttonTextColor?: string;           // ボタン文字色
    categoryBackgroundColor?: string;   // カテゴリ背景色
    categoryTextColor?: string;         // カテゴリ文字色
  };

  // 呼び出しボタン設定
  callButtonSettings?: {
    enabled: boolean;
    notificationMethods: {
      line: boolean;           // LINE通知
      dashboard: boolean;      // ダッシュボード表示
      app: boolean;           // アプリ通知(後日実装)
    };
  };

  // プリンター設定
  receiptPrinterId?: string;  // レシートプリンターID

  // プラン設定
  planSettings?: {
    allowMixedOrders: boolean;  // プラン注文と単品注文の併用を許可するか
  };

  // 原価・バック機能設定（ドリンクバック等）
  costBackSettings?: {
    enabled: boolean;  // 機能ON/OFF
    defaultBackRate: number;  // デフォルトバック率（例: 0.5 = 50%）
    showCostInAnalytics: boolean;  // 分析画面で原価を表示するか
    showBackInAnalytics: boolean;  // 分析画面でバック金額を表示するか
  };

  // インセンティブ設定（売上ベースバック）
  incentiveSettings?: {
    enabled: boolean;  // インセンティブ対応店舗ON/OFF
    /**
     * 累進の当て方（v4.0.0）
     * 'retroactive'(既定): 当期累計で決めたレートを累計全体へ。伝票の切り方で総額が変わらない
     * 'marginal'         : 帯ごとにその帯へ入った額だけへレートを当てる
     * 'wholeAtTier'      : 旧挙動（分割するほど減る）。互換用
     */
    mode?: 'retroactive' | 'marginal' | 'wholeAtTier';
    // アルバイト（時給）ルール
    partTimeRules: {
      dailyAvgThreshold: number;    // 日平均小計の閾値（デフォルト: 30000）
      lowRate: number;              // 閾値以下のレート（デフォルト: 0.15）
      highRate: number;             // 閾値以上のレート（デフォルト: 0.20）
      belowWageRate: number;        // 時給未満の場合のレート（デフォルト: 0.10）
      /** 「時給未満」判定に使う1日の想定勤務時間（デフォルト: 6。夜職の実働に合わせる） */
      standardDailyHours?: number;
    };
    // レギュラースタッフ（月給）ルール
    regularRules: {
      belowSalaryRate: number;      // 月給以下（デフォルト: 0.10）
      aboveSalaryRate: number;      // 月給超え（デフォルト: 0.15）
      tier1Threshold: number;       // ティア1閾値（デフォルト: 600000）
      tier1Rate: number;            // ティア1レート（デフォルト: 0.20）
      tier2Threshold: number;       // ティア2閾値（デフォルト: 1000000）
      tier2Rate: number;            // ティア2レート（デフォルト: 0.25）
    };
  };

  // スタッフアプリ設定
  staffAppSettings?: {
    shiftChangeRequestEnabled: boolean;  // シフト変更リクエスト機能
    salesVisibilityDays: number;         // スタッフの売上データ閲覧可能日数（0=無制限）
    showAnalyticsToStaff: boolean;       // スタッフに売上分析を表示するか
    showBackToStaff: boolean;            // スタッフにバック/インセンティブ明細を表示するか
  };

  // シフト自動公開設定
  shiftAutoPublish?: {
    enabled: boolean;
    scheduleType: 'weekly' | 'biweekly' | 'specific_day' | 'month_end';
    dayOfWeek?: number;         // 0=日, 1=月, ... 6=土
    dayOfMonth?: number;        // 1-28
    biweeklyStartDate?: string; // 'YYYY-MM-DD'
    generateDays: number;       // デフォルト: 7
    notifyStaff: boolean;
    lastAutoPublishedAt?: any;
  };

  // 売上分配の税基準（顧客要望）
  // 'taxExcluded' (既定): スタッフ売上計上は税抜額をベースにする
  // 'taxIncluded': 税込額をベースにする（店舗の運用次第）
  salesAttributionTaxBase?: 'taxIncluded' | 'taxExcluded';

  /**
   * 伝票の取消ポリシー（v4.0.0）
   * 「消す」ではなく「取り消す」。番号は欠番にせず、記録は残す。
   */
  voidPolicy?: {
    allowedRoles: ('owner' | 'manager' | 'staff')[];
    requireApproval: boolean;
    timeLimitMinutes: number | null;
    allowAfterReceipt: boolean;
    requireNote: boolean;
    allowAfterDailyClose: boolean;
  };

  /**
   * バック調整のポリシー（v4.0.0）
   * 自動計算値は上書きせず、調整を差分として積む。
   */
  adjustmentPolicy?: {
    allowedRoles: ('owner' | 'manager')[];
    maxDeltaPerAdjustment: number | null;
    maxRatioOfCalculated: number | null;
    requireReason: boolean;
    allowAfterPeriodClose: boolean;
    /** キャスト本人に調整の内訳を見せるか */
    visibleToStaff: boolean;
  };

  /** 伝票番号の形式（v4.0.0） */
  slipNumberConfig?: {
    digits?: number;
    prefix?: string;
  };

  /**
   * 客が自分のスマホから注文できるか（v4.2.0）
   *
   * 夜職では黒服・レジ・カウンター内のスタッフが入力するのが普通で、
   * 客がボタンを押すことはない。加えて、客からの注文には
   * どのキャストが取ったかの情報が付かないため、そのドリンクは
   * 誰のバックにもならない。夜職は既定で false。
   *
   * 一般飲食は客のQR注文とホールスタッフ入力の併用が普通なので true。
   * false でも、客はメニューの閲覧と自分の伝票の確認はできる。
   */
  customerOrderEnabled?: boolean;

  /**
   * 契約プラン（v4.0.0）
   * 未設定なら、従業員数とアプリ利用者数から自動判定される。
   * 無料条件: 従業員8名以下 または アプリ利用者0名（PCのみ運用）
   */
  planTier?: 'free' | 'standard' | 'pro';

  // 売上集計期間（締日設定、v3.0.0）
  // closingDay: 1-31 の任意 / 'monthEnd' = 月末締め
  // 例: closingDay=15 → 毎月16日〜翌15日が1期間
  // タイムゾーンは shop.timezone を使用
  salesPeriod?: {
    closingDay: number | 'monthEnd';
  };

  // レシート設定
  receiptSettings?: {
    logoUrl?: string;           // レシートに印刷するロゴ画像URL
    headerMessage?: string;     // レシート上部のメッセージ
    footerMessage?: string;     // レシート下部のメッセージ（例: "またのご来店をお待ちしております"）
    showShopAddress?: boolean;  // 店舗住所を表示するか
    showShopPhone?: boolean;    // 店舗電話番号を表示するか
    showTaxDetails?: boolean;   // 税額詳細を表示するか（内税/外税）
    showRegistrationNumber?: string; // インボイス登録番号
    // 顧客要望⑥: 領収書発行済み伝票の削除保護
    deleteProtection?: {
      enabled?: boolean;        // 有効化（デフォルト: true）
      allowOverrideRoles?: ('owner' | 'manager')[]; // 例外的に削除可能なロール
    };
  };

  // 支払い方法マスタ設定
  paymentMethods?: PaymentMethodSetting[];

  // ホームページ設定
  shopHomepage?: {
    heroImage?: string;         // ヒーロー画像URL（後方互換性のため残す）
    heroImages?: string[];      // 複数ヒーロー画像URL（カルーセル用）
    heroVideo?: string;         // ヒーロー動画URL (Firebase Storage)
    mediaType: 'image' | 'video';
    announcements?: {           // お知らせ
      id: string;
      title: string;
      content: string;
      createdAt: Date;
      isActive: boolean;
    }[];
    description?: string;       // SEO用説明文（日本語）
    descriptionEn?: string;     // SEO用説明文（英語）
    descriptionTh?: string;     // SEO用説明文（タイ語）
    descriptionZh?: string;     // SEO用説明文（繁体字中国語）
    descriptionKo?: string;     // SEO用説明文（韓国語）
    showBusinessHours: boolean; // 営業時間の表示ON/OFF
    showPhoneNumber: boolean;   // 電話番号の表示ON/OFF
    showMenus?: boolean;        // メニュー紹介の表示ON/OFF
    showStaff?: boolean;        // スタッフ紹介の表示ON/OFF
    googleMapUrl?: string;      // Google Map埋め込みURL
    seoKeywords?: string[];     // SEO用キーワード
    // 店舗詳細情報
    smokingPolicy?: 'non_smoking' | 'smoking' | 'separated'; // 喫煙・禁煙
    parking?: {
      available: boolean;
      details?: string;
    };
    creditCard?: {
      accepted: boolean;
      types?: string[];
    };
    cashlessPayment?: {
      qrPay?: string[];       // QR決済: PayPay, LINE Pay, etc.
      eMoney?: string[];      // 電子マネー: Suica, iD, etc.
    };
    wifi?: boolean;
    facilities?: string[];

    // カラーテーマ設定
    themeColors?: {
      primaryColor?: string;
      bgDefault?: string;
      bgWhite?: string;
      textPrimary?: string;
      textSecondary?: string;
      textMuted?: string;
      borderColor?: string;
      successColor?: string;
      warningColor?: string;
      errorColor?: string;
      bgImage?: string;
    };

    defaultProductImage?: string; // 商品画像未設定時のデフォルト画像URL
  };

  // 予約設定
  reservationSettings?: {
    allowStaffSelection: boolean;  // スタッフ指名を許可するか
    staffTitle: 'staff' | 'teacher' | 'none';  // スタッフ・先生・敬称略
    allowNoStaffPreference: boolean;  // 指名なしを許可するか
    approvalRequired: boolean;  // 予約承認が必要か（false: 自動承認、true: 手動承認）
  };

  // 通知・リマインダー設定
  notificationSettings?: {
    // 予約受付時の通知
    onReservationCreated: {
      enabled: boolean;
      messageTemplates?: {
        [key in 'ja' | 'en' | 'zh' | 'ko' | 'th']?: string;
      };
    };
    // 予約承認時の通知（手動承認の場合）
    onReservationConfirmed: {
      enabled: boolean;
      messageTemplates?: {
        [key in 'ja' | 'en' | 'zh' | 'ko' | 'th']?: string;
      };
    };
    // リマインダー設定（最大3つ）
    reminders: {
      enabled: boolean;
      timing: 'days_before' | 'hours_before';
      value: number;  // 3日前なら3、1時間前なら1
      messageTemplates?: {
        [key in 'ja' | 'en' | 'zh' | 'ko' | 'th']?: string;
      };
    }[];
    // 自動リマインダー設定
    dayBeforeReminder?: boolean;  // 前日リマインダー（午前9時）
    dayOfReminder?: boolean;      // 当日リマインダー（午前7時）
  };

  // 役職マスタ設定
  roleSettings?: {
    showRole: boolean;  // 役職の表示ON/OFF
    customRoles: string[];  // 店舗独自の役職名リスト（例: ["スタイリスト", "アシスタント", "店長"]）
  };

  // メニューカテゴリマスタ設定
  menuCategorySettings?: {
    categories: string[];  // メニューカテゴリリスト（例: ["カット", "カラー", "パーマ", "トリートメント"]）
  };

  // 予約リソース設定
  reservationResourceSettings?: {
    resourceType: 'staff' | 'room' | 'table' | 'time_only';  // スタッフ・部屋・テーブル・お店おまかせ
    defaultDuration?: number;  // お店おまかせ時のデフォルト所要時間(分)
  };

  // 予約承認設定
  reservationApprovalSettings?: {
    autoApproval: boolean;  // true: 自動承認, false: 手動承認
    paymentDeadlineHours?: number;  // 承認後の決済期限（時間）デフォルト: 24時間
  };

  // 全体予約締切設定
  globalReservationDeadline?: {
    time: string;  // HH:mm形式 (例: "17:00")
    days: number;  // 0=当日, 1=1日前, 2=2日前
  };

  // 来店歓迎メッセージのカスタマイズ（多言語対応）
  welcomeMessages?: {
    ja?: {
      firstVisit?: string;       // 初回来店メッセージ
      returningVisit?: string;   // 再来店メッセージ
    };
    en?: { firstVisit?: string; returningVisit?: string; };
    th?: { firstVisit?: string; returningVisit?: string; };
    'zh-TW'?: { firstVisit?: string; returningVisit?: string; };
    ko?: { firstVisit?: string; returningVisit?: string; };
  };

  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ShopFormData {
  shopName: string;
  businessTypeCode: string;
  ownerName: string;
  ownerEmail: string;
  ownerPhone: string;
  postalCode: string;
  prefecture: string;
  city: string;
  address: string;
  building?: string;
  phoneNumber: string;
  email: string;
  websiteUrl?: string;
  description?: string;

  // Master Data Location
  location?: {
    countryCode: string;
    regionId: string;
    areaId: string;
    districtId?: string;
  };

  // Master Data Business Types
  businessTypes?: {
    categories: string[];
    subcategories: string[];
    detailedTypes: string[];
  };
}

// 旧型定義（後方互換性のため残す）
export interface WorkingHours {
  isWorking: boolean;
  start: string;
  end: string;
}

// 営業時間帯（複数設定可能）
export interface TimeSlot {
  start: string;  // HH:mm形式
  end: string;    // HH:mm形式
  label?: string; // 例: "ランチ", "ディナー"
}

// 曜日ごとの営業時間（複数時間帯対応）
export interface DaySchedule {
  isOpen: boolean;      // 営業日かどうか
  timeSlots: TimeSlot[]; // 営業時間帯のリスト（空配列 = 定休日）
}

export interface Staff {
  id: string;
  shopId: string;
  name: string;
  kanaName: string | null;
  role: string;
  photo: string | null;
  specialties: string[];
  experience: string | null;
  certifications: string[];
  workingHours: {
    monday: WorkingHours;
    tuesday: WorkingHours;
    wednesday: WorkingHours;
    thursday: WorkingHours;
    friday: WorkingHours;
    saturday: WorkingHours;
    sunday: WorkingHours;
  };
  holidays: string[];
  nominationFee: number | null;
  stats: {
    totalReservations: number;
    completedReservations: number;
    averageRating: number;
    reviewCount: number;
  };
  showOnHomepage?: boolean;  // ホームページに公開するか
  introduction?: string;      // スタッフ紹介文
  isActive: boolean;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;

  // 売上分配の個別設定（顧客要望: スタッフ別バック率）
  // カテゴリ側の profitDeductionRate を、このスタッフの取り分計算時に上書き。
  // 例: 通常 0.10（=取り分0.9）だが、特定スタッフだけ 0.20（=取り分0.8）
  // 未設定 → カテゴリ既定値を使用。
  salesProfitDeductionRate?: number;
  // バック率（既存 backRate と統合）— 個別バック計算用、未設定なら shop.costBackSettings.defaultBackRate
  backRate?: number;
}

export interface ReservationRoom {
  id: string;
  shopId: string;
  name: string;
  capacity: number;  // 最大収容人数
  description?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface Menu {
  id: string;
  shopId: string;
  category: string;
  name: string;
  nameEn?: string;
  nameTh?: string;
  nameZhTw?: string;
  nameKo?: string;
  description: string;
  descriptionEn?: string;
  descriptionTh?: string;
  descriptionZhTw?: string;
  descriptionKo?: string;
  price: number;
  duration: number;
  staffIds: string[];
  isActive: boolean;
  sortOrder: number;

  // メッセージテンプレート
  messageTemplates?: {
    confirmation?: string; // 予約確認メッセージテンプレート
    reminder?: string; // リマインダーメッセージテンプレート
    beforeVisit?: string; // 来店前の準備メッセージ
  };

  // デフォルトリマインダー設定
  defaultReminderDays?: number; // デフォルトで何日前にリマインド

  // 支払い設定
  paymentSettings?: {
    type: 'prepay_required' | 'prepay_discount' | 'pay_onsite';  // 先払い必須・先払い割引・当日支払い
    prepayDiscountRate?: number;  // 先払い割引率 (%)
    depositRequired: boolean;  // 予約時に決済を要するか
    depositRate?: number;  // 決済金額の割合 (%) ※depositRequiredがtrueの場合
    stripeEnabled?: boolean;  // Stripe決済を有効化
  };

  // 原価・コミッション設定
  costSettings?: {
    type: 'fixed_cost' | 'commission_rate';  // 固定原価 or コミッション率
    fixedCost?: number;  // 固定原価 (円)
    commissionRate?: number;  // コミッション率 (%)
  };

  createdAt: Date;
  updatedAt: Date;
}

export interface Reservation {
  id: string;
  shopId: string;
  userId: string;
  userName: string;
  userEmail: string;
  userPhone: string;
  userLineId?: string;
  menuId: string;
  menuName: string;
  staffId: string | null;
  staffName: string | null;
  reservationDate: Date;
  startTime: string;
  endTime: string;
  duration: number;
  totalPrice: number;
  numberOfPeople?: number; // 予約人数
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed' | 'no_show';
  notes?: string;
  cancellationReason?: string;
  cancelledBy?: 'shop' | 'customer'; // 店舗都合キャンセル or 顧客都合キャンセル
  checkedInAt?: Date | null;

  // 拡張機能
  customMessage?: string; // 予約ごとのカスタムメッセージ
  reminderSettings?: {
    enabled: boolean;
    daysBefore: number; // 何日前にリマインド
    customMessage?: string; // カスタムリマインダーメッセージ
  };

  // 繰り返し予約
  recurringReservation?: {
    parentId?: string; // 繰り返しグループID
    pattern: 'weekly' | 'biweekly' | 'monthly' | 'custom';
    interval?: number; // カスタムの場合の間隔（日数）
    endDate?: Date; // 繰り返し終了日
    occurrenceCount?: number; // 何回目の予約か
  };

  // 決済情報
  payment?: {
    required: boolean; // 事前決済が必要か
    status: 'pending' | 'completed' | 'failed' | 'refunded'; // 決済ステータス
    amount: number; // 決済金額
    stripePaymentIntentId?: string; // Stripe PaymentIntent ID
    stripeChargeId?: string; // Stripe Charge ID
    sessionId?: string; // Stripe Checkout Session ID
    checkoutUrl?: string; // Stripe Checkout URL
    deadline?: any; // 決済期限 (Firestore Timestamp)
    paidAt?: Date; // 決済完了日時
    refundedAt?: Date; // 返金日時
    refundAmount?: number; // 返金額
  };

  // テーブルアサイン
  tableId?: string; // アサインされたテーブルID
  tableNumber?: string; // テーブル番号（表示用）

  createdAt: Date;
  updatedAt: Date;
  createdBy?: 'customer' | 'shop'; // 予約作成元
}

// Mobile Order Types
// Printer Types
export type PrinterConnectionType = 'network' | 'bluetooth';

export type PrinterModel =
  | 'SII-RP-F10'
  | 'SII-RP-E11'
  | 'SII-RP-D10'
  | 'SII-RP-E10'
  | 'Star-mPOP'       // Star mPOP（Bluetooth、ドロワー内蔵）
  | 'Star-TSP100'     // Star TSP100シリーズ
  | 'Epson-TM-T88'    // EPSON TM-T88シリーズ
  | 'Epson-TM-m30'    // EPSON TM-m30シリーズ
  | 'Other';

export interface Printer {
  id: string;
  shopId: string;
  name: string;

  // 接続タイプ
  connectionType: PrinterConnectionType;

  // ネットワーク接続用
  ipAddress?: string;
  port?: number;

  // Bluetooth接続用
  bluetoothAddress?: string;   // MACアドレス
  bluetoothName?: string;      // デバイス名

  type: 'kitchen' | 'bar' | 'receipt' | 'other';
  model: PrinterModel | string; // プリンターモデル

  // オプション機能
  hasDrawer?: boolean;         // キャッシュドロワー内蔵（mPOP等）

  sortOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProductCategory {
  id: string;
  shopId: string;
  code?: string;  // カテゴリコード（例: "001"）
  name: string;
  nameEn?: string;
  nameTh?: string;
  nameZh?: string; // 中国語
  nameZhTw?: string; // 繁体字中国語 (レガシー)
  nameKo?: string; // 韓国語
  description?: string;
  imageUrl?: string;
  mediaType?: 'image' | 'video';
  displayStatus: 'available' | 'hidden' | 'soldout';
  availableTimeSlots?: {
    type: 'always' | 'specific_times';
    timeSlots?: {
      start: string;
      end: string;
      name?: string;
    }[];
  };
  // 販売制限設定
  salesRestrictions?: {
    // 販売時間制限
    timeRestriction?: {
      enabled: boolean;
      timeSlots?: {
        start: string; // HH:mm形式
        end: string;   // HH:mm形式
      }[];
    };
    // 販売曜日制限
    dayOfWeekRestriction?: {
      enabled: boolean;
      allowedDays?: number[]; // 0=日曜, 1=月曜, ..., 6=土曜
    };
    // 販売日制限
    dateRestriction?: {
      enabled: boolean;
      allowedDates?: string[]; // YYYY-MM-DD形式
    };
    // 販売期間制限
    periodRestriction?: {
      enabled: boolean;
      startDate?: string; // YYYY-MM-DD形式
      endDate?: string;   // YYYY-MM-DD形式
    };
  };
  // サービスチャージ設定
  serviceCharge?: {
    enabled: boolean;
    type: 'percentage' | 'fixed'; // パーセンテージ or 固定額
    value: number; // パーセンテージの場合は0.1=10%、固定額の場合は金額
  };

  // 売上分配設定（顧客要望③④⑤）
  // シャンパン: deductCost=true, profitDeductionRate=0.10
  //   → 小計 = (店売価 - 原価) × (1 - profitDeductionRate)
  // 他ドリンク: deductCost=false, profitDeductionRate=0.10
  //   → 小計 = 店売価 × (1 - profitDeductionRate)
  // 未設定カテゴリ: 原価控除なし、利益控除なし（既存挙動維持）
  salesDistribution?: {
    deductCost?: boolean;          // 原価を控除するか（シャンパン=true）
    profitDeductionRate?: number;  // 利益控除率（例: 0.10 = 10%）
  };
  // 軽減税率適用
  applyReducedTaxRate?: boolean; // 軽減税率を適用するか
  menuTitleCategoryId?: string; // メニュータイトルカテゴリID（shop.menuTitleCategories の id を参照）
  staffOnly?: boolean; // スタッフ専用カテゴリ（お通し、セットメニュー等）
  planOnly?: boolean; // プラン専用カテゴリ（飲み放題プラン有効時のみ表示）
  printerIds?: string[]; // 関連プリンター
  sortOrder: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface ProductOption {
  id: string;
  shopId: string;
  name: string;
  nameEn?: string;
  nameTh?: string;
  nameZh?: string; // 簡体字/繁体字中国語
  nameZhTw?: string; // 繁体字中国語 (レガシー)
  nameKo?: string; // 韓国語
  type: 'single' | 'multiple';
  required: boolean;
  displayStatus: 'available' | 'hidden' | 'soldout';
  choices: {
    id: string;
    code?: string;  // オプション選択肢コード（例: "001"）
    name: string;
    nameEn?: string;
    nameTh?: string;
    nameZh?: string;
    nameZhTw?: string;
    nameKo?: string;
    price: number;
    imageUrl?: string;
    mediaType?: 'image' | 'video';
  }[];
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Product {
  id: string;
  shopId: string;
  categoryId: string;
  code?: string;  // 商品コード（例: "023"、カテゴリ内連番）
  name: string;
  nameEn?: string;
  nameTh?: string;
  nameZh?: string; // 中国語
  nameZhTw?: string; // 繁体字中国語 (レガシー)
  nameKo?: string; // 韓国語
  description?: string;
  descriptionEn?: string;
  descriptionTh?: string;
  descriptionZh?: string;
  descriptionZhTw?: string;
  descriptionKo?: string;
  price: number;
  imageUrl?: string;
  mediaType?: 'image' | 'video';
  optionIds: string[];
  printerIds: string[];
  displayStatus: 'available' | 'hidden' | 'soldout';
  availableTimeSlots?: {
    type: 'always' | 'specific_times';
    timeSlots?: {
      start: string;
      end: string;
      name?: string;
    }[];
  };
  allergens?: string[];
  isSpicy?: boolean;
  isVegetarian?: boolean;

  // ASK商品（価格後入力）
  isAskPrice?: boolean;  // true: 注文時/会計時に価格入力

  // 原価・バック設定
  costSettings?: {
    hasCost: boolean;  // 原価管理対象
    cost: number;  // 商品原価
    backRate: number;  // 基本バック率（例: 0.5 = 50%）
  };

  sortOrder: number;
  isActive: boolean;
  showOnReservationMenu?: boolean;  // 予約メニューに表示するか
  reservationDuration?: number;  // 予約時の所要時間(分)
  assignedStaffIds?: string[];  // 担当可能なスタッフID（予約メニュー用）

  // デリバリー設定
  deliveryAvailable?: boolean;  // デリバリー対応商品
  deliveryPrice?: number;  // デリバリー価格（店内価格と異なる場合）
  createdAt: Date;
  updatedAt: Date;
}

// 席種別
export type SeatType = 'counter' | 'table' | 'privateRoom' | 'semiPrivate' | 'tatami' | 'terrace' | 'other';

export interface Table {
  id: string;
  shopId: string;
  tableNumber: string;
  tableName?: string;           // 表示名（任意）
  seatType?: SeatType;          // 席種別
  minCapacity?: number;         // 最小人数
  maxCapacity?: number;         // 最大人数
  seats: number;                // 後方互換性（maxCapacityと同じ）
  features?: string[];          // 特徴: ['smoking', 'window', 'kids', 'wheelchair', 'horigotatsu']
  qrCodeUrl?: string;
  isActive: boolean;
  isReservable?: boolean;       // 予約可能か
  additionalFee?: number;       // 追加料金
  status?: 'available' | 'occupied' | 'reserved' | 'cleaning';
  sessionStartedAt?: Date;
  currentSessionId?: string;
  groupId?: string;             // テーブル結合用グループID（使用中の結合グループ）

  // 結合設定
  canCombine?: boolean;         // 結合可能か
  combinableWith?: string[];    // 結合可能なテーブルID
  combine2MaxCapacity?: number; // 2結合時の最大人数
  combine2MinCapacity?: number; // 2結合時の最小人数
  combine3PlusMaxCapacity?: number; // 3結合以上時の最大人数
  combine3PlusMinCapacity?: number; // 3結合以上時の最小人数

  // カウンター用（連続席管理）
  totalSeats?: number;          // カウンターの総席数
  seatNumber?: number;          // カウンター内の席番号

  // 並び順
  sortOrder?: number;

  // フロアMAPレイアウト（グリッド座標。未設定=自動配置でMAP表示）
  posX?: number;
  posY?: number;
  width?: number;   // グリッド単位
  height?: number;  // グリッド単位
  shape?: 'rect' | 'round';

  createdAt: Date;
  updatedAt: Date;
}

export interface CartItem {
  product: Product;
  quantity: number;
  selectedOptions: {
    [optionId: string]: string[]; // optionId -> choice ids
  };
  note?: string;
}

export interface Order {
  id: string;
  shopId: string;
  tableId: string;
  tableNumber: string;
  orderNumber?: string; // 注文番号
  items: OrderItem[];
  subtotal: number;
  tax: number;
  serviceCharge?: number; // サービス料
  total: number;
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'served' | 'completed' | 'cancelled';
  note?: string;
  notes?: string; // 備考（後方互換性のため）

  // 認証情報（LINE認証の場合）
  lineUserId?: string; // LINE ユーザーID
  lineDisplayName?: string; // LINE 表示名
  linePictureUrl?: string; // LINE プロフィール画像URL

  // 認証情報（SMS認証の場合）
  phoneNumber?: string; // 電話番号
  firebaseUid?: string; // Firebase認証UID

  // 認証タイプ
  authType?: 'line' | 'sms';

  // 注文者情報（代理注文）
  orderedBy?: {
    isStaffOrder?: boolean;
    userId?: string;
    staffId?: string;
    staffName?: string;
  };

  // 支払い
  paymentStatus?: 'unpaid' | 'paid';

  // タイムスタンプ
  orderedAt?: Date; // 注文日時
  completedAt?: Date; // 完了日時
  printedAt?: Date; // 印刷日時
  createdAt: Date;
  updatedAt: Date;

  // 顧客要望⑥: 領収書発行済みフラグ（削除保護用）
  receiptIssued?: boolean;
  receiptIssuedAt?: any;
  receiptIssuedBy?: string;
  receiptIssuedType?: 'paper' | 'digital' | 'both';
}

export interface OrderItem {
  productId: string;
  productName: string;
  productNameEn?: string;
  productNameTh?: string;
  price: number;
  unitPrice?: number; // 単価（後方互換性のため）
  quantity: number;
  options: {
    optionId: string;
    optionName: string;
    choices: {
      id: string;
      name: string;
      price: number;
    }[];
  }[];
  selectedOptions?: { // 後方互換性のため
    choiceName: string;
    price: number;
  }[];
  note?: string;
  notes?: string; // 備考（後方互換性のため）
  printerId?: string;

  // 個別商品管理用フィールド
  itemId?: string; // 個別商品ID（ラーメン1/4, 2/4などを区別）
  itemIndex?: number; // 商品内のインデックス（1, 2, 3, 4...）
  itemStatus?: 'pending' | 'preparing' | 'ready' | 'served' | 'cancelled'; // 個別ステータス
  itemPrintedAt?: any; // 個別印刷時刻

  // ASK商品用（後から入力された価格）
  askPrice?: number;  // 入力された価格

  // 原価・バック情報
  costInfo?: {
    cost: number;  // 商品原価
    baseBackRate: number;  // 基本バック率
  };

  // 販売スタッフ情報（複数対応、分配率）
  salesStaff?: {
    staffId: string;
    staffName: string;
    shareRate: number;  // 分配率（例: 0.8 = 80%）
    backRate?: number;  // 個別バック率（オーナーが後から変更可能）
    backAmount?: number;  // 計算済みバック金額
  }[];

  // カテゴリID（リアルタイム参照のキャッシュ）
  categoryId?: string;

  // 売上振替（顧客要望④⑤）
  // 設定された場合、このアイテムのインセンティブベースは指定スタッフへ全額付け替え
  // バースデー時の本指名客→バースデースタッフ振替などで使用
  transferToStaffId?: string;
  transferToStaffName?: string;
  transferReason?: 'birthday' | 'manual' | string;
}

// Call Request (呼び出しボタン機能)
export interface CallRequest {
  id: string;
  shopId: string;
  tableId: string;
  tableNumber: string;
  requestedAt: Date;
  status: 'pending' | 'acknowledged' | 'completed';
  acknowledgedAt?: Date;
  completedAt?: Date;
}

// Customer (顧客情報 - 店舗側で管理)
export interface Customer {
  id: string;  // Firebase Auth UID

  // 基本情報
  displayName: string;
  photoUrl?: string;

  // 認証情報
  authType: 'line' | 'sms';
  lineUserId?: string;
  phoneNumber?: string;
  email?: string;

  // 店舗固有の統計情報
  shopStats: {
    [shopId: string]: {
      totalVisits: number;
      totalSpent: number;
      lastVisit: Date;
      favoriteMenus?: string[];  // メニューID配列
    };
  };

  createdAt: Date;
  updatedAt: Date;
}

// 来店履歴
export interface Visit {
  id: string;

  // 関連情報
  shopId: string;
  shopName: string;
  userId: string;  // Firebase Auth UID

  // 認証情報
  authType: 'line' | 'sms';
  lineUserId?: string;
  phoneNumber?: string;

  // 来店情報
  visitDate: Date;
  visitType: 'reservation' | 'mobile_order';

  // 予約の場合
  reservationId?: string;
  menuIds?: string[];
  menuNames?: string[];
  staffId?: string;
  staffName?: string;

  // モバイルオーダーの場合
  orderIds?: string[];
  tableNumber?: string;
  totalAmount?: number;

  // セッション管理
  sessionId?: string;
  firstOrderAt?: Date;
  lastOrderAt?: Date;

  notes?: string;

  createdAt: Date;
  updatedAt: Date;
}

// 飲み放題・食べ放題プラン
export interface DrinkFoodPlan {
  id: string;
  shopId: string;
  name: string;           // 例: "飲み放題プランA"
  nameEn?: string;
  nameTh?: string;
  type: 'all_you_can_drink' | 'all_you_can_eat' | 'course';
  imageUrl?: string;
  price: number;
  timeLimit: number;      // 分単位（120分など）
  lastOrderBefore: number; // ラストオーダーは終了何分前か（例: 30 = 終了30分前）
  description?: string;
  descriptionEn?: string;
  descriptionTh?: string;

  // 商品選択
  includedProductIds: string[];  // このプランに含まれる商品ID
  basePlanId?: string;          // 継承元プランID（プランBがプランAを継承する場合）

  // 表示・販売設定
  displayOrder: number;
  isActive: boolean;
  salesRestrictions?: {
    timeRestriction?: { enabled: boolean; timeSlots: { start: string; end: string }[] };
    dayOfWeekRestriction?: { enabled: boolean; allowedDays: number[] };
  };

  // 延長設定
  extensionSettings?: {
    enabled: boolean;           // 延長機能を有効化
    extensionTime: number;      // 延長時間（分単位、例: 30分）
    extensionPrice: number;     // 延長料金
    maxExtensions?: number;     // 最大延長回数（未設定=無制限）
    billingMode?: 'fixed' | 'tiered'; // 課金モード（デフォルト: tiered）
    hourlyRate?: number;        // tiered時の1時間あたり料金
    graceMinutes?: number;      // グレース分数（無料時間）
    tiers?: { maxMinutes: number; feeRatio: number }[]; // 階段ティア
  };

  createdAt: Date;
  updatedAt: Date;
}

// プラン注文（テーブルセッション単位で管理）
export interface PlanOrder {
  id: string;
  shopId: string;
  tableId: string;
  tableNumber: string;
  planId: string;
  planName: string;
  planPrice: number;
  planType: 'all_you_can_drink' | 'all_you_can_eat' | 'course';

  // ゲスト識別（人単位プラン対応）
  guestLabel: string;         // 表示名（デフォルト: "Aさん", "Bさん"...、変更可能）
  guestIndex: number;         // 連番（0始まり、自動採番）

  // セッション
  sessionId?: string;

  // 指名情報
  nominatedStaffId?: string;    // 指名スタッフID
  nominatedStaffName?: string;  // 指名スタッフ名
  nominationFee?: number;       // 指名料（デフォルト0）
  nominationType?: 'honshimei' | 'banai' | 'none'; // 本指名 / 場内指名 / なし

  // 時間管理
  startTime: Date;          // プラン開始時刻
  endTime: Date;            // プラン終了時刻
  lastOrderTime: Date;      // ラストオーダー時刻
  timeLimit: number;        // 制限時間（分）

  // 注文された商品（全て0円で記録）— オプショナル（Flutter側は書き込まない）
  orderedItems?: {
    productId: string;
    productName: string;
    quantity: number;
    orderedAt: Date;
  }[];

  // ステータス
  status: 'active' | 'last_order_notified' | 'completed' | 'cancelled' | 'paid';

  // 延長履歴
  extensions?: {
    extendedAt: Date;
    extensionTime: number;      // 延長時間（分）
    extensionPrice: number;     // 延長料金
    extendedBy: string;         // 延長を実行したスタッフID
    extendedByName: string;     // 延長を実行したスタッフ名
  }[];
  totalExtensionTime?: number;  // 合計延長時間（分）
  totalExtensionPrice?: number; // 合計延長料金

  // 会計調整データ
  checkoutAdjustment?: {
    originalExtensionCount: number;
    adjustedExtensionCount: number;
    adjustedExtensionPrice: number;
    billingMode?: 'fixed' | 'tiered';
    hourlyRate?: number;
    originalOvertimeMinutes?: number;
    adjustedOvertimeMinutes?: number;
    adjustedBy: string;
    adjustedByName: string;
    adjustedAt: any;
    reason?: string;
  };

  // 認証情報（誰が注文したか）
  lineUserId?: string;
  lineDisplayName?: string;
  linePictureUrl?: string;
  phoneNumber?: string;
  firebaseUid?: string;
  authType?: 'line' | 'sms';

  // 延長設定（プランマスタから）
  extensionSettings?: {
    enabled: boolean;
    extensionTime: number;   // 1回の延長時間（分）
    extensionPrice: number;  // 1回の延長料金
    maxExtensions?: number;  // 最大延長回数
    billingMode?: 'fixed' | 'tiered';
    hourlyRate?: number;
    graceMinutes?: number;
    tiers?: { maxMinutes: number; feeRatio: number }[];
  };

  // 指名変更履歴
  nominationHistory?: {
    previousStaffId?: string;
    previousStaffName?: string;
    previousType: string;
    previousFee: number;
    newStaffId?: string;
    newStaffName?: string;
    newType: string;
    newFee: number;
    changedAt: any;
    changedBy: string;
    changedByName: string;
  }[];

  // 支払い情報
  paymentStatus?: 'unpaid' | 'paid';
  paymentMethod?: 'cash' | 'credit_card' | 'card' | 'transfer';
  paidAt?: Date;

  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  completedAt?: Date;
}

// 支払い方法設定
export interface PaymentMethodSetting {
  id: string;                   // 一意のID
  code: string;                 // システムコード（例: 'cash', 'card', 'paypay', 'promptpay'）
  name: string;                 // 表示名（例: '現金', 'クレジットカード', 'PayPay'）
  nameEn?: string;              // 英語名
  nameTh?: string;              // タイ語名
  // 'receivable' = ツケ（掛け売り）。会計は成立するが入金は後日。
  type: 'cash' | 'card' | 'qr' | 'transfer' | 'receivable' | 'other';  // 支払いタイプ
  isActive: boolean;            // 有効/無効
  sortOrder: number;            // 表示順
  icon?: string;                // アイコン名（MUI Icon名）
  color?: string;               // ボタン色（HEX）
  feeRate?: number;             // 決済手数料率（例: 0.1 = 10%）、0または未設定で手数料なし
}

// ブラックリスト
export interface BlacklistedUser {
  id: string;
  shopId: string;

  // ブロック対象の識別子（複数指定可能）
  lineUserId?: string;
  phoneNumber?: string;
  firebaseUid?: string;

  // ユーザー情報（記録用）
  displayName?: string;
  photoUrl?: string;

  // ブロック理由と詳細
  reason: string;
  blockedByName: string;
  blockedByUid: string;
  blockedByRole: 'owner' | 'manager' | 'staff';

  // 関連注文（証拠として）
  relatedOrderIds: string[];

  // ステータス
  isActive: boolean;

  // 解除予定日（オプション）
  unblockAt?: Date;

  // メタデータ
  createdAt: Date;
  updatedAt: Date;
  unblockedAt?: Date;
  unblockedBy?: string;
  unblockedReason?: string;
}

// 受付不可ブロック
export interface BlockedSlot {
  id: string;
  shopId: string;
  staffId?: string;       // null = 店舗全体、指定 = 特定スタッフのみ
  staffName?: string;     // 表示用
  date: Date;
  startTime: string;      // "12:00"
  endTime: string;        // "14:00"
  reason?: string;        // "休憩" "満席" "研修" など
  isAllDay: boolean;      // 終日ブロック
  createdBy: string;
  createdAt: Date;
}

// ============================================================
// ナイト業態モデル (v4.0.0)
// スナック / メンズパブ / キャバクラ / ホストクラブ向け。
// 以前は「同伴・ボトルキープ・ツケ」が Firestore 側に存在せず、
// 同伴とボトルバックは NightPro(Laravel) の手当マスタにしか無かった。
// ツケとボトルキープはどちらにも無く、スナックでは初月に運用が止まる。
// ============================================================

/** ボトルキープ（キープボトル） */
export interface BottleKeep {
  id: string;
  shopId: string;

  // 誰のボトルか
  customerId?: string;        // customers / shops/{id}/customers の顧客ID
  customerName: string;       // 台帳に手書きで載る名前（顧客ID未登録でも運用できる）
  phoneNumber?: string;

  // 何のボトルか
  productId?: string;
  productName: string;
  /** 本数。同銘柄を複数キープする場合 */
  quantity: number;

  // ボトルを入れた会計
  openedCheckoutId?: string;
  openedAt: Date;
  /** 入れたときの担当キャスト（ボトルバックの対象） */
  openedByStaffId?: string;
  openedByStaffName?: string;
  openedPrice: number;

  // 残量管理
  /** 0-100 の目分量。棚卸しで更新する */
  remainingPercent: number;

  // 期限
  /** キープ期限。過ぎたら店の判断で処分する */
  expiresAt?: Date;
  /** 期限切れ通知を送ったか */
  expiryNotifiedAt?: Date;

  // 状態
  status: 'kept' | 'consumed' | 'expired' | 'disposed' | 'transferred';

  /** 保管場所（棚番号など） */
  location?: string;
  note?: string;

  // 利用履歴
  usageHistory?: {
    usedAt: Date;
    checkoutId?: string;
    remainingPercentAfter: number;
    servedByStaffId?: string;
    servedByStaffName?: string;
    note?: string;
  }[];

  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
}

export const bottleKeepStatusLabels: Record<BottleKeep['status'], string> = {
  kept: 'キープ中',
  consumed: '空き',
  expired: '期限切れ',
  disposed: '処分済み',
  transferred: '移管',
};

/** 掛け売り・ツケ（未収金） */
export interface Receivable {
  id: string;
  shopId: string;

  // 誰のツケか
  customerId?: string;
  customerName: string;
  phoneNumber?: string;
  /** 法人ツケの場合の会社名 */
  companyName?: string;

  // 元になった会計
  checkoutId: string;
  checkoutType: 'table' | 'pos';
  /** 営業日 YYYY-MM-DD */
  businessDate: string;

  // 金額
  amount: number;          // 発生額
  paidAmount: number;      // 入金済み額
  /** amount - paidAmount。クエリで使うため冗長に持つ */
  balance: number;

  // 誰の責任か（回収責任者。キャストに紐づけると回収率が可視化できる）
  responsibleStaffId?: string;
  responsibleStaffName?: string;
  /** 承認した責任者（PC側で操作する） */
  approvedBy: string;
  approvedByName: string;

  dueDate?: Date;
  status: 'open' | 'partially_paid' | 'paid' | 'written_off';

  payments?: {
    paidAt: Date;
    amount: number;
    method: 'cash' | 'credit_card' | 'transfer' | 'other';
    receivedBy: string;
    receivedByName: string;
    note?: string;
  }[];

  /** 貸倒処理 */
  writtenOffAt?: Date;
  writtenOffBy?: string;
  writtenOffReason?: string;

  note?: string;
  createdAt: Date;
  updatedAt: Date;
}

export const receivableStatusLabels: Record<Receivable['status'], string> = {
  open: '未収',
  partially_paid: '一部入金',
  paid: '完済',
  written_off: '貸倒',
};

/** 卓に付いたキャスト1人ぶんの在席記録 */
export interface TableCastAssignment {
  staffId: string;
  staffName: string;
  /**
   * primary = 本指名 / banai = 場内指名 / help = ヘルプ / back = 付け回り
   * キャバクラの卓は複数キャストが時間帯で重なるのが常態で、
   * それが売上配分の根拠になる。1卓1指名では表現できない。
   */
  role: 'primary' | 'banai' | 'help' | 'back';
  assignedAt: Date;
  releasedAt?: Date | null;
  assignedBy?: string;
  /** 同伴で入店した場合 */
  isDohan?: boolean;
  /** アフター（閉店後）に繋がった場合 */
  isAfter?: boolean;
}

/** 卓の来店セッション（複数キャストの重なりを持つ） */
export interface TableSession {
  id: string;
  shopId: string;
  tableId: string;
  tableName?: string;

  customerId?: string;
  customerName?: string;
  reservedCount?: number;
  actualCount: number;

  startTime: Date;
  endTime?: Date;
  /** 営業日 YYYY-MM-DD */
  businessDate: string;

  status: 'active' | 'ordering' | 'checkout' | 'completed' | 'cancelled';

  /** 主担当（本指名）。casts にも primary として入る */
  primaryStaffId?: string;
  primaryStaffName?: string;

  /** 卓に付いた全キャスト。会計のバック計算がこれを読む */
  casts: TableCastAssignment[];

  /** この卓で使ったキープボトル */
  bottleKeepIds?: string[];

  paymentId?: string;
  totalAmount?: number;
  nominationTotal?: number;

  createdBy?: string;
  createdAt: Date;
  updatedAt: Date;
}

/** 日払い（当日精算）。夜職では出勤当日に一部を渡す運用が普通にある。 */
export interface Payout {
  id: string;
  shopId: string;
  staffId: string;
  staffName: string;

  /** 営業日 YYYY-MM-DD */
  businessDate: string;
  /** どの給与期間から引くか（salesPeriod.periodKey） */
  salesPeriodKey: string;

  amount: number;
  method: 'cash' | 'transfer' | 'other';

  /**
   * 内訳の目安。実際の締め計算では payout の合計を
   * 「支給額 - 前払い」として差し引くだけで、内訳は参考値。
   */
  breakdown?: {
    /** 当日の時給ぶん */
    wage?: number;
    /** 当日のバックぶん */
    back?: number;
    /** それ以外（前借り） */
    advance?: number;
  };

  status: 'paid' | 'cancelled';

  paidBy: string;
  paidByName: string;
  note?: string;

  cancelledAt?: Date;
  cancelledBy?: string;
  cancelReason?: string;

  createdAt: Date;
  updatedAt: Date;
}

export const payoutMethodLabels: Record<Payout['method'], string> = {
  cash: '現金',
  transfer: '振込',
  other: 'その他',
};
